import { Box, Button, Checkbox, Divider, FormControl, FormControlLabel, FormGroup, FormHelperText, MenuItem, Radio, RadioGroup, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ErrorMessage, Field, FieldProps, useField, useFormikContext } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { clearProductHandlingInformation, getDropDownInformation, getProductHandlingInformation } from '../../store/formSlice';
import styles from './ProductHandlingInformation.module.css';
import RequiredLabel from './RequiredLabel';
import { useAppDispatch } from '../../store/store';

interface TemperatureRequirement {
  temperatureMin: string;
  temperatureMax: string;
}

interface ProductHandlingInformationProps {
  onNext: () => void;
  onBack: () => void;
  values: any;
  errors: any;
  touched: any;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  currentStep: number;
  steps: string[];
}

const ProductHandlingInformation: React.FC<ProductHandlingInformationProps> = ({
  errors,
  onBack,
  onNext,
  touched,
  values,
  setFieldValue,
  currentStep,
  steps
}) => {
  const [commodityOptions, setCommodityOptions] = useState<string[]>([]);
  const [containerTypeOptions, setContainerTypeOptions] = useState<string[]>([]);
  const [freightClassOptions, setFreightClassOptions] = useState<string[]>([]);
  const [specialHandlingOptions, setSpecialHandlingOptions] = useState<string[]>([]);
  //const specialHandlingOptions = ["Liftgates", "Pallet Jack"];
  const [field] = useField('productDimension');
  const dropDownOptions = useSelector(getDropDownInformation)
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  
  const productHandlingState = useSelector((state: any) => state.productHandling);
  const {commodity,containerType} = useSelector(getProductHandlingInformation)
  const { setFieldError, setFieldTouched,setErrors,resetForm } = useFormikContext();
  const [showOtherField, setShowOtherField] = useState(false);
  const hasUpdatedRef = useRef(false);

  const [shouldUpdate, setShouldUpdate] = useState(false);
  
  const dimensionFields = ['Length', 'Height', 'Width'];
  const temperatureFields = ['Minimum' ,'Maximum'   ];
  useEffect(() => {
    if (dropDownOptions) {
      const { Commodity, ContainerType, FreightClass, SpecialHandlingType } = dropDownOptions;
      setCommodityOptions(Commodity || []);
      setContainerTypeOptions(ContainerType || []);
      setFreightClassOptions(FreightClass || []);
      setSpecialHandlingOptions(SpecialHandlingType || []);
      setErrors({});
    }
  }, [dropDownOptions, setErrors]);

  useEffect(() => {
    //dispatch(clearProductHandlingInformation());
    if (commodity && containerType) {
      const validCommodity = commodityOptions.includes(commodity) ? commodity : '';
      const validContainerType = containerTypeOptions.includes(containerType) ? containerType : '';
      const fieldUpdates = {
        commodity: validCommodity,
        containerType: validContainerType,
      };
      Object.entries(fieldUpdates).forEach(([field, value]) => {
        if (!values[field]) {
          setFieldValue(field, value, false);
        }
      });

      // Set showOtherField to true if commodity is "Others"
      
      if (commodity === 'Others') {
        setShowOtherField(true);
      } else {
        setShowOtherField(false);
      }
    }

     // Set default value for freightClass
     if (!values.freightClass) {
      setFieldValue('freightClass', 'Freight Class 65');
    }
  }, [commodity, containerType, commodityOptions, containerTypeOptions, setFieldValue, values]);


  const handleBack = () => {
    resetForm({ values }); // Reset the form state
    setErrors({}) // Clear all errors
    onBack();
  };

  const handleFormValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFieldValue(name, value);
    hasUpdatedRef.current = false; // Allow manual updates
    setFieldTouched(name, true); // Mark the field as touched
    setFieldError(name, ''); // Clear any existing error for the field

    if (name === 'commodity' && value === 'Others') {
      setShowOtherField(true);
    } else if (name === 'commodity') {
      setShowOtherField(false);
    }
  };

  const handleFlagSelection = (event: React.ChangeEvent<HTMLInputElement>, name: string) => {
    const value = event.target.value;
    setFieldValue(name, value);
    setFieldTouched(name, true);

    if (name === 'reeferRequired' && value === 'no') {
      setFieldValue('temperatureRequirement.temperatureMax', '');
      setFieldValue('temperatureRequirement.temperatureMin', '');
      setFieldTouched('temperatureRequirement.temperatureMax', false);
      setFieldTouched('temperatureRequirement.temperatureMin', false);
    }

    if (name === 'hazmatFlag' && value === 'no') {
      setFieldValue('hazmatUnNumber', '');
      setFieldValue('hazmatClass', '');
      setFieldValue('packagingGroup', '');
      setFieldValue('handlingComments', '');
      setFieldTouched('hazmatUnNumber', false);
      setFieldTouched('hazmatClass', false);
      setFieldTouched('packagingGroup', false);
      setFieldTouched('handlingComments', false);
      setFieldError('hazmatUnNumber', '');
      setFieldError('hazmatClass', '');
      setFieldError('packagingGroup', '');
      setFieldError('handlingComments', '');
    }

    if (name === 'expediteServiceFlag' && value === 'no') {
      setFieldValue('expediteApproverEmail', '');
      setFieldTouched('expediteApproverEmail', false);
      setFieldError('expediteApproverEmail', '');
    }

    if (name === 'fullTruckLoadFlag' && value === 'yes') {
      setFieldValue('isLoadParcel', '');
      setFieldTouched('isLoadParcel', false);
      setFieldError('isLoadParcel', '');
      setFieldValue('freightClass', '');
      setFieldValue('dedicatedTransportFlag', '');
      setFieldValue('dedicatedApproverEmail', '');
      setFieldTouched('freightClass', false);
      setFieldTouched('dedicatedTransportFlag', false);
      setFieldTouched('dedicatedApproverEmail', false);
      setFieldError('freightClass', '');
      setFieldError('dedicatedTransportFlag', '');
      setFieldError('dedicatedApproverEmail', '');
    }

    if (name === 'isLoadParcel' && value === 'yes') {
      setFieldValue('freightClass', '');
      setFieldValue('dedicatedTransportFlag', '');
      setFieldValue('dedicatedApproverEmail', '');
      setFieldTouched('freightClass', false);
      setFieldTouched('dedicatedTransportFlag', false);
      setFieldTouched('dedicatedApproverEmail', false);
      setFieldError('freightClass', '');
      setFieldError('dedicatedTransportFlag', '');
      setFieldError('dedicatedApproverEmail', '');
    }

    if (name === 'teamDeliveryFlag' && value === 'no') {
      setFieldValue('teamDeliveryApproverEmail', '');
      setFieldTouched('teamDeliveryApproverEmail', false);
      setFieldError('teamDeliveryApproverEmail', '');
    }

    if (name === 'specialHandlingRequired' && value === 'no') {
      setFieldValue('specialHandlingTypeList', []);
      setFieldTouched('specialHandlingTypeList', false);
      setFieldError('specialHandlingTypeList', '');
    }
  };

  return (
    <div style={{ height: 'auto' }}>
      <h2 className={styles.titleCard}>Product & Handling Information</h2>
      <div style={{ paddingBottom: '10rem' }}>
        <Grid container spacing={3}>
          <Grid size={1}></Grid>
          <Grid size={10}>
            <h2 className={styles.title}>Product Information</h2>
          </Grid>
          <Grid size={1}></Grid>
          <Grid size={1}></Grid>
          <Grid size={10}>
            <Grid container spacing={3}>
              <Grid size={2.9}>
                <div style={{ marginTop: '18px' }}>
                  <RequiredLabel label="Commodity" mandatory={true} />
                </div>
              </Grid>
              <Grid size={2.9}>
                <TextField id="commodity" name="commodity" select variant="outlined" size="small" margin="dense" InputProps={{
                  style: {
                    fontSize: '0.8rem'
                  },
                }} required autoComplete="off" fullWidth value={values.commodity}
                  onChange={handleFormValueChange} >
                  {commodityOptions.map((commodity, index) => (
                    <MenuItem key={index} value={commodity}>
                      {commodity}
                    </MenuItem>
                  ))}
                </TextField>
                <ErrorMessage name="commodity" component="div" className="error-message" />
              </Grid>
              <Grid size={0.4}>
                <Divider orientation="vertical" />
              </Grid>
              <Grid size={2.9}>
                <div style={{ marginTop: '18px' }}>
                  <RequiredLabel label="Quantity" mandatory={true} />
                </div>
              </Grid>
              <Grid size={2.9}>
                <TextField id="quantity" name="quantity" type={"text"} variant="outlined" size="small"
                  fullWidth margin="dense" InputProps={{
                    style: {
                      fontSize: '0.8rem'
                    }
                  }} autoComplete="off" value={values.quantity} placeholder="Enter Quantity" onChange={handleFormValueChange} />
                <ErrorMessage name="quantity" component="div" className="error-message" />
              </Grid>
              <Grid size={12}>
                <Divider orientation="horizontal" />
              </Grid>
              {showOtherField && (
                <React.Fragment>
                  <Grid size={2.9}>
                    <div style={{ marginTop: '18px' }}>
                      <RequiredLabel label="Other" mandatory={true} />
                    </div>
                  </Grid>
                  <Grid size={2.9}>
                  <TextField id="commodityOtherValue" name="commodityOtherValue" type="text" variant="outlined" size="small"
                      fullWidth margin="dense" InputProps={{
                        style: {
                          fontSize: '0.8rem'
                        }
                      }} autoComplete="off" value={values.commodityOtherValue} placeholder="Enter Others" onChange={handleFormValueChange} />
                    <ErrorMessage name="commodityOtherValue" component="div" className="error-message" />
                  </Grid>
                  <Grid size={0.4}>
                    <Divider orientation="vertical" />
                  </Grid>
                  <Grid size={2.9}>
                    <div style={{ marginTop: '18px' }}>
                      
                    </div>
                  </Grid>
                  <Grid size={2.9}>
                   
                  </Grid>
                  <Grid size={12}>
                    <Divider orientation="horizontal" />
                  </Grid>
                </React.Fragment>
              )}
              <Grid size={2.9}>
                <div style={{ marginTop: '18px' }}>
                  <RequiredLabel label="Container Type" mandatory={true} />
                </div>
              </Grid>
              <Grid size={2.9}>
                <TextField id="containerType" name="containerType" select variant="outlined" size="small" margin="dense" InputProps={{
                  style: {
                    fontSize: '0.8rem'
                  },
                }} required autoComplete="off" fullWidth value={values.containerType || ""}
                  onChange={handleFormValueChange}
                  placeholder='Enter Contianer Type'
                >
                  <MenuItem value="" disabled>
                    Enter container type
                  </MenuItem>
                  {containerTypeOptions.map((containerType, index) => (
                    <MenuItem key={index} value={containerType}>
                      {containerType}
                    </MenuItem>
                  ))}
                </TextField>
                <ErrorMessage name="containerType" component="div" className="error-message" />
              </Grid>
              <Grid size={0.4}>
                <Divider orientation="vertical" />
              </Grid>
              <Grid size={2.9}>
                <div style={{ marginTop: '18px' }}>
                  <RequiredLabel label="Product Dimensions(In)" mandatory={true} />
                </div>
              </Grid>
              {dimensionFields.map((label, index) => (
          <Grid key={label} size={0.96}>
            <FormControl variant="outlined" size="small" margin="dense" fullWidth>
              <Field name={`productDimension.${index}`}>
                {({ field, meta }: FieldProps) => (
                  <>
                    <TextField
                      {...field}
                      type="text"
                      variant="outlined"
                      size="small"
                      margin="dense"
                      InputProps={{
                        style: {
                          fontSize: '0.8rem',
                        },
                        
                      }}
                      autoComplete="off"
                      placeholder={label}
                      
                    />
                   
                  </>
                )}
              </Field>
            </FormControl>
          </Grid>
        ))}
              {/* <Grid size={0.96}>
                <FormControl variant="outlined" size="small" margin="dense" fullWidth>
                  <Field name="productDimension.height">
                    {({ field }: FieldProps) => (
                      <TextField
                        {...field}
                        type="text"
                        variant="outlined"
                        size="small"
                        margin="dense"
                        InputProps={{
                          style: {
                            fontSize: '0.8rem',
                          },
                        }}
                        autoComplete="off"
                        placeholder="Height"
                      />
                    )}
                  </Field>
                  {touched.productDimension?.height && errors.productDimension?.height && (
                   <ErrorMessage name="productDimension.height" component={FormHelperText} className="error-message" />
                  )}
                 
                </FormControl>
              </Grid> */}
              {/* <Grid size={0.96}>
                <FormControl variant="outlined" size="small" margin="dense" fullWidth>
                  <Field name="productDimension.width">
                    {({ field }: FieldProps) => (
                      <TextField
                        {...field}
                        type="text"
                        variant="outlined"
                        size="small"
                        margin="dense"
                        InputProps={{
                          style: {
                            fontSize: '0.8rem',
                          },
                        }}
                        autoComplete="off"
                        placeholder="Width"
                      />
                    )}
                  </Field>
                  {touched.productDimension?.width && errors.productDimension?.width && (
                   <ErrorMessage name="productDimension.width" component={FormHelperText} className="error-message" />
                  )}
                  
                </FormControl>
              </Grid> */}
              <Grid  size={6}>
      </Grid>
              <Grid  size={6}>
        <ErrorMessage name="productDimension" component="div" className="error-message" />
      </Grid>
              <Grid size={12}>
                <Divider orientation="horizontal" />
              </Grid>
              <Grid size={2.9}>
                <div style={{ marginTop: '18px' }}>
                  <RequiredLabel label="Total Weight (LBS)" mandatory={true} />
                </div>
              </Grid>
              <Grid size={2.9}>
                <TextField id="totalWeightLbs" name="totalWeightLbs" type={"text"} variant="outlined" size="small"
                  fullWidth margin="dense" InputProps={{
                    style: {
                      fontSize: '0.8rem'
                    }
                  }} autoComplete="off" value={values.totalWeightLbs} placeholder="Enter Total Weight" onChange={handleFormValueChange} />
                <ErrorMessage name="totalWeightLbs" component="div" className="error-message" />
              </Grid>
              <Grid size={0.4}>
                <Divider orientation="vertical" />
              </Grid>
              <Grid size={2.9}>
                <div style={{ marginTop: '18px' }}>
                  <RequiredLabel label="Load Value ($)" mandatory={false} />
                </div>
              </Grid>
              <Grid size={2.9}>
                <TextField id="loadValue" name="loadValue" type={"text"} variant="outlined" size="small"
                  fullWidth margin="dense" InputProps={{
                    style: {
                      fontSize: '0.8rem'
                    }
                  }} autoComplete="off" value={values.loadValue} placeholder="Enter Amount" onChange={handleFormValueChange} />
                <ErrorMessage name="loadValue" component="div" className="error-message" />
              </Grid>
              <Grid size={12}>
                <Divider orientation="horizontal" />
              </Grid>
              <Grid size={12}>
                <RequiredLabel label="Additional Comments" mandatory={false} />
                <TextField id="productAdditionalComments" name="productAdditionalComments" type={"textarea"} variant="outlined" size="small"
                  fullWidth margin="dense" InputProps={{
                    style: {
                      fontSize: '0.8rem'
                    }
                  }} autoComplete="off" value={values.productAdditionalComments} placeholder="Please Enter your Comments" onChange={handleFormValueChange} multiline
                  minRows={3}
                />
                <ErrorMessage name="productAdditionalComments" component="div" className="error-message" />
              </Grid>
              <Grid size={12}>
                <Divider orientation="horizontal" />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={1}>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={1}></Grid>
          <Grid size={10}>
            <h2 className={styles.title}>Handling Information</h2>
          </Grid>
          <Grid size={1}></Grid>
          <Grid size={1}></Grid>
          <Grid size={10}>
            <Grid container spacing={3}>
              <Grid size={6}>
                <Grid container spacing={2}>
                  <Grid size={5.8}>
                    <div style={{ marginTop: '18px' }}>
                      <RequiredLabel label="Reefer" mandatory={true} />
                    </div>
                  </Grid>
                  <Grid size={5.8}>
                    <RadioGroup row value={values.reeferRequired} onChange={(e) => handleFlagSelection(e, 'reeferRequired')} sx={{ display: 'flex', flexDirection: 'row' }}>
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }} />
                      <FormControlLabel value="no" control={<Radio />} label="No" sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }} />
                    </RadioGroup>
                    <ErrorMessage name="reeferRequired" component="div" className="error-message" />

                  </Grid>
                  <Grid size={0.4}>
                    <Divider orientation="vertical" />
                  </Grid>



                  {values.reeferRequired === 'yes' && (
                    <>
                      <Grid size={5.8}>
                        <div style={{ marginTop: '18px' }}>
                          <RequiredLabel label="Temperature Requirement (°F)" mandatory={true} />
                        </div>
                      </Grid>
                      {temperatureFields.map((label, index) => (
            <Grid size={2.9} key={label}>
              <FormControl variant="outlined" size="small" margin="dense" fullWidth>
              <Field name={`temperatureRequirement.${index}`}>
                {({ field, meta }: FieldProps) => (
                  <>
                    <TextField
                      {...field}
                      type="text"
                      variant="outlined"
                      size="small"
                      margin="dense"
                      InputProps={{
                        style: {
                          fontSize: '0.8rem',
                        },
                        
                      }}
                      autoComplete="off"
                      placeholder={label}
                      
                    />
                   
                  </>
                )}
              </Field>
            </FormControl>
            </Grid>
          ))}
                     
                      <Grid  size={12}>
                      <ErrorMessage name="temperatureRequirement" component="div" className="error-message" />
      </Grid>
              {/* <Grid  size={6}>
        
      </Grid> */}
                    </>
                  )}
                </Grid>
              </Grid>
              <Grid size={6}>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <div style={{ marginTop: '15px' }}>
                      <RequiredLabel label="Hazmat" mandatory={true} />
                    </div>
                  </Grid>
                  <Grid size={6}>
                    <RadioGroup row value={values.hazmatFlag} onChange={(e) => handleFlagSelection(e, 'hazmatFlag')} sx={{ display: 'flex', flexDirection: 'row' }}>
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }} />
                      <FormControlLabel value="no" control={<Radio />} label="No" sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }} />
                    </RadioGroup>
                    <ErrorMessage name="hazmatFlag" component="div" className="error-message" />
                  </Grid>
                  {/* <Grid size={12}>
                    <ErrorMessage name="hazmatFlag" component="div" className="error-message" />
                  </Grid> */}

                  {values.hazmatFlag === 'yes' && <>
                    <Grid size={6}>
                      <div style={{ marginTop: '15px' }}>
                        <RequiredLabel label="Hazmat UN#" mandatory={true} />
                      </div>
                    </Grid>
                    <Grid size={6}>
                      <TextField id="hazmatUnNumber" name="hazmatUnNumber" type={"text"} variant="outlined" size="small"
                        margin="dense" InputProps={{
                          style: {
                            fontSize: '0.8rem'
                          }
                        }} fullWidth autoComplete="off" value={values.hazmatUnNumber} placeholder="Enter UN Number" onChange={handleFormValueChange} />
                      <ErrorMessage name="hazmatUnNumber" component="div" className="error-message" />
                    </Grid>
                    {/* <Grid size={12}>
                      <ErrorMessage name="hazmatUnNumber" component="div" className="error-message" />
                    </Grid> */}


                    <Grid size={6}>
                      <div style={{ marginTop: '15px' }}>
                        <RequiredLabel label="Hazmat Class" mandatory={true} />
                      </div>
                    </Grid>
                    <Grid size={6}>
                      <TextField id="hazmatClass" name="hazmatClass" type={"text"} variant="outlined" size="small"
                        margin="dense" InputProps={{
                          style: {
                            fontSize: '0.8rem'
                          }
                        }} fullWidth autoComplete="off" value={values.hazmatClass} placeholder="Enter Hazmat Class" onChange={handleFormValueChange} />
                      <ErrorMessage name="hazmatClass" component="div" className="error-message" />
                    </Grid>
                    {/* <Grid size={12}>
                      <ErrorMessage name="hazmatClass" component="div" className="error-message" />
                    </Grid> */}

                    <Grid size={6}>
                      <div style={{ marginTop: '15px' }}>
                        <RequiredLabel label="Packaging Group" mandatory={true} />
                      </div>
                    </Grid>
                    <Grid size={6}>
                      <TextField id="packagingGroup" name="packagingGroup" type={"text"} variant="outlined" size="small"
                        margin="dense" InputProps={{
                          style: {
                            fontSize: '0.8rem'
                          }
                        }} fullWidth autoComplete="off" value={values.packagingGroup} placeholder="Enter Packaging Group" onChange={handleFormValueChange} />
                      <ErrorMessage name="packagingGroup" component="div" className="error-message" />
                    </Grid>
                    {/* <Grid size={12}>
                      <ErrorMessage name="packagingGroup" component="div" className="error-message" />
                    </Grid> */}

                    <Grid size={6}>
                      <div style={{ marginTop: '15px' }}>
                        <RequiredLabel label="Handling Comments" mandatory={true} />
                      </div>
                    </Grid>
                    <Grid size={6}>
                      <TextField id="handlingComments" name="handlingComments" type={"text"} variant="outlined" size="small"
                        margin="dense" InputProps={{
                          style: {
                            fontSize: '0.8rem'
                          }
                        }} fullWidth autoComplete="off" value={values.handlingComments} placeholder="Enter Handling Comments" onChange={handleFormValueChange} />
                      <ErrorMessage name="handlingComments" component="div" className="error-message" />
                    </Grid>
                    {/* <Grid size={12}>
                      <ErrorMessage name="handlingComments" component="div" className="error-message" />
                    </Grid> */}
                  </>
                  }
                </Grid>
              </Grid>
              <Grid size={12}>
                <Divider orientation="horizontal" />
              </Grid>
              <Grid size={6}>
                <Grid container spacing={2}>
                  <Grid size={5.8}>
                    <div style={{ marginTop: '15px', paddingBottom: '30px' }}>
                      <RequiredLabel label="Expedite Transit Service" mandatory={true} />
                    </div>
                  </Grid>
                  <Grid size={5.8}>
                    <RadioGroup row value={values.expediteServiceFlag} onChange={(e) => handleFlagSelection(e, 'expediteServiceFlag')} sx={{ display: 'flex', flexDirection: 'row' }}>
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }} />
                      <FormControlLabel value="no" control={<Radio />} label="No" sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }} />
                    </RadioGroup>
                    <ErrorMessage name="expediteServiceFlag" component="div" className="error-message" />
                  </Grid>
                  {/* <Grid size={12}>
                    <ErrorMessage name="expediteServiceFlag" component="div" className="error-message" />
                  </Grid> */}
                  <Grid size={0.4}>
                    <Divider orientation="vertical" />
                  </Grid>

                  {values.expediteServiceFlag === 'yes' &&
                    <>
                      <Grid size={5.8}>
                        <div style={{ marginTop: '15px' }}>
                          <RequiredLabel label="Approver's Email Address" mandatory={true} />
                        </div>
                      </Grid>
                      <Grid size={5.8}>
                        <TextField id="expediteApproverEmail" name="expediteApproverEmail" type={"text"} variant="outlined" size="small"
                          margin="dense" InputProps={{
                            style: {
                              fontSize: '0.8rem'
                            }
                          }} fullWidth autoComplete="off" value={values.expediteApproverEmail} placeholder="Enter Email Address" onChange={handleFormValueChange} />
                        <ErrorMessage name="expediteApproverEmail" component="div" className="error-message" />
                      </Grid>
                      <Grid size={0.4}>
                      </Grid>
                      {/* <Grid size={12}>
                        <ErrorMessage name="expediteApproverEmail" component="div" className="error-message" />
                      </Grid> */}
                    </>
                  }

                </Grid>
              </Grid>
              <Grid size={6}>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <div style={{ marginTop: '15px' }}>
                      <RequiredLabel label="Full Truck Load" mandatory={true} />
                      <p style={{
                        fontFamily: 'Helvetica Neue',
                        fontSize: '10px',
                        fontWeight: 500,
                        lineHeight: '12.21px',
                        textAlign: 'left',
                        textUnderlinePosition: 'from-font',
                        textDecorationSkipInk: 'none',
                        color: '#626262',
                        marginBottom: '0px'
                      }}>
                        11 pallets and above or more than 20k Lbs is FTL (Max weight - 44k Lbs)
                      </p>
                    </div>
                  </Grid>
                  <Grid size={6}>
                    <RadioGroup row value={values.fullTruckLoadFlag} onChange={(e) => handleFlagSelection(e, 'fullTruckLoadFlag')} sx={{ display: 'flex', flexDirection: 'row' }}>
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }} />
                      <FormControlLabel value="no" control={<Radio />} label="No" sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }} />
                    </RadioGroup>
                    <ErrorMessage name="fullTruckLoadFlag" component="div" className="error-message" />
                  </Grid>
                  {/* <Grid size={12}>
                    <ErrorMessage name="fullTruckLoadFlag" component="div" className="error-message" />
                  </Grid> */}


                  {values.fullTruckLoadFlag === 'no' && <>
                    <Grid size={6}>
                      <div style={{ marginTop: '15px' }}>
                        <RequiredLabel label="Is the Load Parcel ?" mandatory={true} />

                        {values.isLoadParcel === 'yes' && <p style={{
                          fontFamily: 'Helvetica Neue',
                          fontSize: '10px',
                          fontWeight: 500,
                          lineHeight: '12.21px',
                          textAlign: 'left',
                          textUnderlinePosition: 'from-font',
                          textDecorationSkipInk: 'none',
                          color: '#626262',
                        }}>
                          Container type has to be box/piece, and weight has to be 150lbs or below
                          L+H+D combined cannot exceed 165 inches.
                        </p>}
                      </div>
                    </Grid>
                    <Grid size={6}>
                      <RadioGroup row value={values.isLoadParcel} onChange={(e) => handleFlagSelection(e, 'isLoadParcel')} sx={{ display: 'flex', flexDirection: 'row' }}>
                        <FormControlLabel value="yes" control={<Radio />} label="Yes" sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }} />
                        <FormControlLabel value="no" control={<Radio />} label="No" sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }} />
                      </RadioGroup>
                      <ErrorMessage name="isLoadParcel" component="div" className="error-message" />
                    </Grid>
                    {/* <Grid size={12}>
                      <ErrorMessage name="isLoadParcel" component="div" className="error-message" />
                    </Grid> */}

                    {values.isLoadParcel === 'no' &&
                      <>
                        <Grid size={6}>
                          <div style={{ marginTop: '15px' }}>
                            <RequiredLabel label="Freight Class" mandatory={true} />
                          </div>
                        </Grid>
                        <Grid size={6}>
                          <TextField id="freightClass" name="freightClass" select variant="outlined" size="small" margin="dense" InputProps={{
                            style: {
                              fontSize: '0.8rem'
                            },
                          }} required autoComplete="off" fullWidth value={values.freightClass}
                            onChange={handleFormValueChange} >
                            {freightClassOptions.map((freightClass, index) => (
                              <MenuItem key={index} value={freightClass}>
                                {freightClass}
                              </MenuItem>
                            ))}
                          </TextField>
                          <ErrorMessage name="freightClass" component="div" className="error-message" />
                        </Grid>
                        {/* <Grid size={12}>
                          <ErrorMessage name="freightClass" component="div" className="error-message" />
                        </Grid> */}

                        <Grid size={6}>
                          <div style={{ marginTop: '15px' }}>
                            <RequiredLabel label="Is Dedicated Transport Required?" mandatory={true} />
                          </div>
                        </Grid>
                        <Grid size={6}>
                          <RadioGroup row value={values.dedicatedTransportFlag} onChange={(e) => handleFlagSelection(e, 'dedicatedTransportFlag')} sx={{ display: 'flex', flexDirection: 'row' }}>
                            <FormControlLabel value="yes" control={<Radio />} label="Yes" sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }} />
                            <FormControlLabel value="no" control={<Radio />} label="No" sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }} />
                          </RadioGroup>
                          <ErrorMessage name="dedicatedTransportFlag" component="div" className="error-message" />
                        </Grid>
                        {/* <Grid size={12}>
                          <ErrorMessage name="dedicatedTransportFlag" component="div" className="error-message" />
                        </Grid> */}

                        {values.dedicatedTransportFlag === 'yes' &&
                          <>
                            <Grid size={6}>
                              <div style={{ marginTop: '15px' }}>
                                <RequiredLabel label="Approver's Email Address" mandatory={true} />
                              </div>
                            </Grid>
                            <Grid size={6}>
                              <TextField id="dedicatedApproverEmail" name="dedicatedApproverEmail" type={"text"} variant="outlined" size="small"
                                margin="dense" InputProps={{
                                  style: {
                                    fontSize: '0.8rem'
                                  }
                                }} fullWidth autoComplete="off" value={values.dedicatedApproverEmail} placeholder="Enter Email Address" onChange={handleFormValueChange} />
                              <ErrorMessage name="dedicatedApproverEmail" component="div" className="error-message" />
                            </Grid>
                            {/* <Grid size={12}>
                              <ErrorMessage name="dedicatedTransportApproverEmail" component="div" className="error-message" />
                            </Grid> */}
                          </>
                        }
                      </>
                    }
                  </>
                  }
                </Grid>
              </Grid>
              <Grid size={12}>
                <Divider orientation="horizontal" />
              </Grid>
              <Grid size={6}>
                <Grid container spacing={2}>
                  <Grid size={5.8}>
                    <div style={{ marginTop: '15px' }}>
                      <RequiredLabel label="Team Delivery Service" mandatory={true} />
                      <p style={{
                        fontFamily: 'Helvetica Neue',
                        fontSize: '10px',
                        fontWeight: 500,
                        lineHeight: '12.21px',
                        textAlign: 'left',
                        textUnderlinePosition: 'from-font',
                        textDecorationSkipInk: 'none',
                        color: '#626262',
                        paddingBottom: '0px'
                      }}>
                        Average per day 1000 miles for team driver. Approval is required to select this option
                      </p>
                    </div>
                  </Grid>
                  <Grid size={5.8}>
                    <RadioGroup row value={values.teamDeliveryFlag} onChange={(e) => handleFlagSelection(e, 'teamDeliveryFlag')} sx={{ display: 'flex', flexDirection: 'row' }}>
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }} />
                      <FormControlLabel value="no" control={<Radio />} label="No" sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }} />
                    </RadioGroup>
                    <ErrorMessage name="teamDeliveryFlag" component="div" className="error-message" />
                  </Grid>
                  {/* <Grid size={12}>
                    <ErrorMessage name="teamDeliveryFlag" component="div" className="error-message" />
                  </Grid> */}
                  <Grid size={0.4}>
                    <Divider orientation="vertical" />
                  </Grid>

                  {values.teamDeliveryFlag === 'yes' &&
                    <>
                      <Grid size={5.8}>
                        <div style={{ marginTop: '15px' }}>
                          <RequiredLabel label="Approver's Email Address" mandatory={true} />
                        </div>
                      </Grid>
                      <Grid size={5.8}>
                        <TextField id="teamDeliveryApproverEmail" name="teamDeliveryApproverEmail" type={"text"} variant="outlined" size="small"
                          margin="dense" InputProps={{
                            style: {
                              fontSize: '0.8rem'
                            }
                          }} fullWidth autoComplete="off" value={values.teamDeliveryApproverEmail} placeholder="Enter Email Address" onChange={handleFormValueChange} />
                        <ErrorMessage name="teamDeliveryApproverEmail" component="div" className="error-message" />
                      </Grid>
                      {/* <Grid size={12}>
                        <ErrorMessage name="teamDeliveryApproverEmail" component="div" className="error-message" />
                      </Grid> */}
                      <Grid size={0.4}>

                      </Grid>
                    </>
                  }
                </Grid>
              </Grid>


              <Grid size={6}>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <div style={{ marginTop: '15px' }}>
                      <RequiredLabel label="Special Handling Required" mandatory={true} />
                    </div>
                  </Grid>
                  <Grid size={6}>
                    <RadioGroup row value={values.specialHandlingRequired} onChange={(e) => handleFlagSelection(e, 'specialHandlingRequired')} sx={{ display: 'flex', flexDirection: 'row' }}>
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }} />
                      <FormControlLabel value="no" control={<Radio />} label="No" sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }} />
                    </RadioGroup>
                    <ErrorMessage name="specialHandlingRequired" component="div" className="error-message" />
                  </Grid>
                  {/* <Grid size={12}>
                    <ErrorMessage name="specialHandlingRequired" component="div" className="error-message" />
                  </Grid> */}


                  {
                    values.specialHandlingRequired === 'yes' && specialHandlingOptions &&
                    <Grid size={10}>
                      <div className={styles.specialHandlingTypeCls}>
                        <FormControl component="fieldset">
                          <FormGroup>
                            {specialHandlingOptions?.map((option) => (
                              <FormControlLabel
                                key={option}
                                control={
                                  <Checkbox
                                    checked={values?.specialHandlingType?.includes(option)}
                                    onChange={(e: { target: { checked: any; value: any; }; }) => {
                                      const { checked, value } = e.target;
                                      const currentValues = values?.specialHandlingTypeList || [];
                                      setFieldValue(
                                        'specialHandlingTypeList',
                                        checked
                                          ? [...currentValues, value]
                                          : currentValues.filter((v: any) => v !== value)
                                      );
                                    }}
                                    name="specialHandlingTypeList"
                                    value={option}
                                    size='small'
                                  />
                                }
                                label={option}
                                sx={{ "& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }}
                              />
                            ))}
                          </FormGroup>

                        </FormControl>

                      </div>
                      <ErrorMessage name="specialHandlingTypeList" component="div" className="error-message" />
                    </Grid>
                  }

                </Grid>
              </Grid>
              <Grid size={12}>
                <Divider orientation="horizontal" />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>

      <Box sx={{
        position: "fixed",
        bottom: "60px", // Space above the footer
        left: '8rem',
        width: "80%",
        display: "flex",
        justifyContent: "space-between", // Left and right alignment
        alignItems: "center"
      }}>
        <Button variant="contained" style={{
          backgroundColor: '#F6EB39', color: '#2E2E38', fontFamily: 'Helvetica Neue',
          fontSize: '14px', fontWeight: 700, lineHeight: '22px', textAlign: 'left',
        }} onClick={handleBack} sx={{ mr: 2 }}>
          Back
        </Button>
        <Button variant="contained" style={{
          backgroundColor: '#F6EB39', color: '#2E2E38', fontFamily: 'Helvetica Neue',
          fontSize: '14px', fontWeight: 700, lineHeight: '22px', textAlign: 'left',
        }} onClick={onNext}>
          {currentStep === steps.length - 1 ? 'Submit' : 'Next'}
        </Button>
      </Box>
    </div>

  );
};

export default ProductHandlingInformation;



